import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ApiCallError, callFunction } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatCprDisplay } from '@/lib/cpr';

export type ReceivedPatientIdKind = 'cpr' | 'erstatnings_cpr' | 'none';

/**
 * G1/G2 (DIS01-M 3.6): the CPR or erstatnings-CPR on an opened received (X)DIS,
 * or a relative's PersonIdentifier when `relativeIndex` is set.
 * Fetches via communicate-epikriser reveal_cpr when the detail opens (audited);
 * the value only lives in component state and is never logged.
 */
export function ReceivedMessageCpr({
  clinicId,
  exchangeId,
  idKind,
  relativeIndex,
}: {
  clinicId: string;
  exchangeId: string;
  idKind: ReceivedPatientIdKind;
  /** Index in document.relatives for pårørende CPR reveal (audited per relative). */
  relativeIndex?: number;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canReveal = hasPermission(clinicId, 'patients_cpr', 'view');
  const [cpr, setCpr] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const isRelative = relativeIndex !== undefined;
  const replacement = !isRelative && idKind === 'erstatnings_cpr';
  const hasIdentifier = isRelative || idKind !== 'none';

  useEffect(() => {
    setCpr(null);
  }, [exchangeId, relativeIndex]);

  useEffect(() => {
    if (!canReveal || !hasIdentifier || cpr) return;
    let cancelled = false;
    setPending(true);
    void (async () => {
      try {
        const payload: Record<string, string | number> = {
          action: 'reveal_cpr',
          clinic_id: clinicId,
          exchange_id: exchangeId,
          source: isRelative ? 'received-epikrise-relative' : 'received-epikrise-detail',
        };
        if (isRelative) payload.relative_index = relativeIndex;
        const r = await callFunction<Record<string, string | number>, { cpr: string }>(
          'communicate-epikriser',
          payload,
        );
        if (!cancelled) setCpr(r.cpr);
      } catch (err) {
        if (!cancelled) toast.error(err instanceof ApiCallError ? err.message : t('patients.errors.unknown'));
      } finally {
        if (!cancelled) setPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canReveal, clinicId, cpr, exchangeId, hasIdentifier, isRelative, relativeIndex, t]);

  if (!hasIdentifier) {
    return <span className="text-ds-muted">{t('medcom.epikriser.no_cpr', 'Intet CPR-nummer i meddelelsen')}</span>;
  }

  const placeholder = replacement ? t('referrals.replacement_cpr_present') : t('referrals.cpr_present');

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="received-message-cpr">
      <div className="inline-flex items-center rounded-full bg-ds-green-tint px-2 py-0.5 text-xs font-medium text-ds-green-ink">
        {pending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-1 h-3 w-3" />}
        {cpr ? <span className="font-mono">{formatCprDisplay(cpr)}</span> : placeholder}
      </div>
      {replacement && (
        <span
          className="rounded bg-ds-amber-tint px-1.5 py-0.5 text-xs font-medium text-ds-amber-ink"
          data-testid="received-message-replacement-cpr"
        >
          {t('referrals.replacement_cpr')}
        </span>
      )}
      {!canReveal && (
        <span className="text-xs text-ds-muted">{t('referrals.cpr_no_permission', 'Du har ikke adgang til at se CPR-nummeret')}</span>
      )}
    </div>
  );
}
